import { Directive, inject } from '@angular/core';
import { DragItemDirective } from './drag-item.directive';

/**
 * Marks an element as the drag handle for its containing `[appDragItem]`.
 *
 * Without this directive, the whole `[appDragItem]` element would respond to
 * drag gestures. With it, the item is only draggable when the gesture starts
 * from this handle element.
 *
 * Injects `DragItemDirective` from the nearest ancestor, enabling drag on
 * mousedown/touchstart and disabling it on mouseup (when no drag starts).
 * The `dragend` event on the item also disables it automatically.
 *
 * @example
 * ```html
 * <li appDragItem [dragItemIndex]="i">
 *   <span appDragHandle class="drag-handle">
 *     <app-icon name="grip-vertical" />
 *   </span>
 *   {{ item.label }}
 * </li>
 * ```
 */
@Directive({
  selector: '[appDragHandle]',
  standalone: true,
  host: {
    '(mousedown)': 'onMouseDown()',
    '(touchstart)': 'onTouchStart()',
    '(mouseup)': 'onMouseUp()',
    '(touchend)': 'onTouchEnd()',
  },
})
export class DragHandleDirective {
  private readonly item = inject(DragItemDirective);

  onMouseDown(): void {
    this.item.enableDrag();
  }

  onTouchStart(): void {
    this.item.enableDrag();
  }

  /** Resets draggable if the press didn't become a drag (no dragstart/dragend will fire). */
  onMouseUp(): void {
    this.item.disableDrag();
  }

  onTouchEnd(): void {
    this.item.disableDrag();
  }
}
